import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useDatasetsDataCache } from '@global/hooks/queries/useDatasetsDataCache'
import { KEY as GET_TEMPLATE_QUERY_KEY } from '@global/hooks/queries/useTemplateQuery'
import { api } from 'api'
import { Query as GetTemplatesQueryKey } from 'pages/datasets/types'
import * as SnackbarActions from '../../../../global/snackbar/snackbar.actions'
import { saveTemplate, resetTemplateChangedState } from '../template.actions'
import * as TemplateActions from '../template.actions'

interface TemplateLogResponse {
  status: string
  message?: string
}

interface TemplateResponse {
  data: Record<string, any>
}

const LogStatus = {
  SUCCESS: 'Success',
  FAILED: 'Failed',
  RUNNING: 'Running',
} as const

type LogStatus = (typeof LogStatus)[keyof typeof LogStatus]

export const useSaveTemplate = ({ dispatch }) => {
  const initialState = {
    saving: true,
    initialSaveComplete: false,
    initialSaveSuccess: false,
    message: '',
    requestComplete: false,
    integrationFailed: {
      status: false,
      message: '',
    },
  }

  const [saveStatus, setSaveStatus] = useState(initialState)

  const queryClient = useQueryClient()
  const { invalidateCache } = useDatasetsDataCache()

  function showFailedToSaveError(err: any, dispatch: any, message: string) {
    dispatch(
      SnackbarActions.showSnackbar({
        type: 'ERROR',
        message: err?.message || message,
      }),
    )
  }

  const longPoll = async (templateId: string) => {
    const url = `/api/v1/account/template/${templateId}/template-logs`
    let { data } = await api.get<TemplateLogResponse>(url, {
      params: {
        poll: true,
      },
    })
    let pollCount = 0

    while (data.status === LogStatus.RUNNING && pollCount < 4) {
      const response = await api.get<TemplateLogResponse>(url, {
        params: {
          poll: true,
        },
      })
      data = response.data
      pollCount++
    }

    if (data.status === LogStatus.FAILED) {
      showFailedToSaveError(data, dispatch, 'Template integrations failed to validate.')
      setSaveStatus(prevState => ({
        ...prevState,
        requestComplete: true,
        integrationFailed: { status: true, message: data.message || '' },
        saving: false,
      }))
    } else if (data.status === LogStatus.RUNNING) {
      dispatch(
        SnackbarActions.showSnackbar({
          type: 'INFO',
          message: 'Template is taking a while to save. Please check back later.',
        }),
      )
    } else if (data.status === LogStatus.SUCCESS) {
      dispatch(
        SnackbarActions.showSnackbar({
          type: 'SUCCESS',
          duration: 10000,
          message: 'Template saved successfully',
        }),
      )
      setSaveStatus(prevState => ({
        ...prevState,
        requestComplete: true,
        integrationFailed: { status: false, message: data.message || '' },
        saving: false,
      }))

      try {
        const approversResponse = await api.get(`/api/v1/account/approvers/${templateId}`)
        const templateResponse = await api.get<TemplateResponse>(
          `/api/v1/account/template/${templateId}`,
        )

        const payload = {
          ...templateResponse.data,
          approvers: approversResponse.data,
        }
        dispatch(TemplateActions.setTemplate(payload))
      } catch (error) {
        console.error('Error fetching template data:', error)
      }
    }
  }

  const handleSaveCallback = async templateState => {
    setSaveStatus(prevState => ({ ...prevState, saving: true })) // Disable save button
    const templateId = await dispatch(
      saveTemplate(templateState, {
        afterSave: () => {
          /**
           * When we save a template, we need to invalidate the datasets and templates query
           */
          invalidateCache({ datasetId: templateState.rollup_dataset_id })
          void queryClient.invalidateQueries({ queryKey: [GetTemplatesQueryKey.TEMPLATES] })

          queryClient.removeQueries({
            queryKey: [GET_TEMPLATE_QUERY_KEY, String(templateState?.template?.id)],
          })
        },
      }),
    )

    if (templateId) {
      setSaveStatus(prevState => ({
        ...prevState,
        initialSaveComplete: true,
        initialSaveSuccess: true,
      }))
      void longPoll(templateId)
      dispatch(resetTemplateChangedState())
    } else {
      setSaveStatus(prevState => ({
        ...prevState,
        initialSaveComplete: true,
        initialSaveSuccess: false,
        message: 'Template failed to save',
        saving: false,
      }))
    }
  }

  return {
    handleSaveCallback,
    saveStatus,
    setSaveStatus,
    longPoll,
    LogStatus,
    showFailedToSaveError,
  }
}
